import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { IoClose } from "react-icons/io5";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";

const ConversionCheckpoints = ({ isOpen, onClose }) => {
    const [forms, setForms] = useState([]);
    const [selectedForm, setSelectedForm] = useState(null);
    const [checkpoints, setCheckpoints] = useState(null);
    const [isLoadingForms, setIsLoadingForms] = useState(false);
    const [isLoadingCheckpoints, setIsLoadingCheckpoints] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            loadForms();
        }
    }, [isOpen]);

    useEffect(() => {
        if (selectedForm) {
            loadCheckpoints(selectedForm);
        } else {
            setCheckpoints(null);
        }
    }, [selectedForm]);

    const loadForms = async () => {
        setIsLoadingForms(true);
        setError(null);
        try {
            const data = await api.chat.listCheckpointForms();
            setForms(data.forms || []);
        } catch (err) {
            console.error('Error loading forms:', err);
            setError(err.message);
            setForms([]);
        } finally {
            setIsLoadingForms(false);
        }
    };

    const loadCheckpoints = async (formName) => {
        setIsLoadingCheckpoints(true);
        setError(null);
        try {
            const data = await api.chat.getFormCheckpoints(formName);
            setCheckpoints(data);
        } catch (err) {
            console.error('Error loading checkpoints:', err);
            setError(err.message);
            setCheckpoints(null);
        } finally {
            setIsLoadingCheckpoints(false);
        }
    };

    const handleFormSelect = (formName) => {
        setSelectedForm(formName);
    };

    const handleClose = () => {
        setSelectedForm(null);
        setCheckpoints(null);
        setError(null);
        onClose();
    };

    const getCheckpointStatus = (checkpoint) => {
        if (checkpoint.completed) {
            return {
                icon: <IoCheckmarkCircle className="w-6 h-6" />,
                color: '#10b981', // green
                text: 'Completed'
            };
        } else {
            return {
                icon: <IoCloseCircle className="w-6 h-6" />,
                color: '#6b7280', // gray
                text: 'Pending'
            };
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleClose}
        >
            <div 
                className="flex flex-col bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh]"
                style={{ backgroundColor: 'var(--bg-primary)' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 border-b" style={{ borderBottomColor: 'var(--border-color)' }}>
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Conversion Checkpoints
                    </h3>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                        style={{ color: 'var(--text-primary)' }}
                    >
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="flex flex-1 overflow-hidden">
                    {/* Forms List Sidebar */}
                    <div 
                        className="w-64 border-r overflow-y-auto p-4"
                        style={{ 
                            borderRightColor: 'var(--border-color)',
                            backgroundColor: 'var(--bg-secondary)'
                        }}
                    >
                        <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>
                            Forms
                        </h4>
                        {isLoadingForms ? (
                            <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                Loading forms...
                            </div>
                        ) : error && !forms.length ? (
                            <div className="text-center py-8 text-red-500 text-sm">
                                {error}
                            </div>
                        ) : forms.length === 0 ? (
                            <div className="text-center py-8" style={{ color: 'var(--text-secondary)' }}>
                                No forms found
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {forms.map((formName) => (
                                    <button
                                        key={formName}
                                        onClick={() => handleFormSelect(formName)}
                                        className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-200 ${
                                            selectedForm === formName
                                                ? 'font-semibold'
                                                : 'hover:bg-gray-100'
                                        }`}
                                        style={{
                                            backgroundColor: selectedForm === formName 
                                                ? 'var(--blue-primary)' 
                                                : 'transparent',
                                            color: selectedForm === formName 
                                                ? 'white' 
                                                : 'var(--text-primary)',
                                        }}
                                    >
                                        {formName}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Checkpoints Content Area */}
                    <div className="flex-1 flex flex-col overflow-hidden p-6">
                        {!selectedForm ? (
                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                Select a form from the list to view checkpoints
                            </div>
                        ) : isLoadingCheckpoints ? (
                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                Loading checkpoints...
                            </div>
                        ) : error && !checkpoints ? (
                            <div className="flex-1 flex items-center justify-center text-red-500">
                                {error}
                            </div>
                        ) : checkpoints ? (
                            <div className="flex-1 overflow-y-auto">
                                <div className="mb-6">
                                    <h4 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                                        {checkpoints.form_name}
                                    </h4>
                                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                        Created: {new Date(checkpoints.created_at).toLocaleString()}
                                    </p>
                                    {checkpoints.updated_at && (
                                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                            Last Updated: {new Date(checkpoints.updated_at).toLocaleString()}
                                        </p>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <h5 className="text-md font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                                        Conversion Progress
                                    </h5>
                                    
                                    {Object.values(checkpoints.checkpoints || {}).map((checkpoint) => {
                                        const status = getCheckpointStatus(checkpoint);
                                        return (
                                            <div
                                                key={checkpoint.id}
                                                className="flex items-center gap-4 p-4 rounded-lg border"
                                                style={{
                                                    backgroundColor: 'var(--bg-secondary)',
                                                    borderColor: 'var(--border-color)'
                                                }}
                                            >
                                                <div style={{ color: status.color }}>
                                                    {status.icon}
                                                </div>
                                                <div className="flex-1">
                                                    <div className="font-medium" style={{ color: 'var(--text-primary)' }}>
                                                        {checkpoint.name}
                                                    </div>
                                                    {checkpoint.completed_at && (
                                                        <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                                                            Completed: {new Date(checkpoint.completed_at).toLocaleString()}
                                                        </div>
                                                    )}
                                                </div>
                                                <div 
                                                    className="text-sm font-medium"
                                                    style={{ color: status.color }}
                                                >
                                                    {status.text}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1 flex items-center justify-center" style={{ color: 'var(--text-secondary)' }}>
                                No checkpoint data available
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConversionCheckpoints;

